import {CrudRightsEnum} from "../../../enums/user/crud-rights.enum";
import {UserTypeEnum} from "../../../enums/user/user-type.enum";
import {UserModel} from "./user.model";

export interface ConsolidatedUserAccess {
  id: number;
  companyId: string;
  type: UserTypeEnum;
  crudRights: CrudRightsEnum;
  crudRightsText?: string;
  changedBy: string;
  changedTimestamp: string;
  isValid?: boolean;
}

export interface ConsolidatedUserModelInterface {
  [key: string]: string | number | Array<ConsolidatedUserAccess> | Array<string> | undefined;

  userId: string;
  assetTypeAccess: Array<ConsolidatedUserAccess>;
  assetAccess: Array<ConsolidatedUserAccess>;
  companyIds: Array<string>;
  invalidEntries: Array<ConsolidatedUserAccess>;
  assetAccessDisplay?: string;
}

export class ConsolidatedUserModel implements ConsolidatedUserModelInterface {
  [key: string]: string | number | Array<ConsolidatedUserAccess> | Array<string> | undefined;

  public userId = "";
  public assetTypeAccess: Array<ConsolidatedUserAccess> = [];
  public assetAccess: Array<ConsolidatedUserAccess> = [];
  public companyIds: Array<string> = [];
  public invalidEntries: Array<ConsolidatedUserAccess> = [];
  public assetAccessDisplay?: string;

  constructor(userId: string = "") {
    this.userId = userId;
  }
}

export const createUserAccess = (
  userId: string,
  companyId: string = "",
  crudRights: CrudRightsEnum = CrudRightsEnum.None,
  id: number = -1,
  changedBy: string = "",
  changedTimestamp: string = "",
  type?: UserTypeEnum
): ConsolidatedUserAccess => {
  const accessType = type || (companyId && companyId.trim() !== "" ? UserTypeEnum.Company_Assets : UserTypeEnum.All_Assets);

  const finalCrudRights =
    accessType === UserTypeEnum.Company_Assets && crudRights === CrudRightsEnum.None ? CrudRightsEnum.Read : crudRights;

  return {
    id,
    companyId: accessType === UserTypeEnum.All_Assets ? "" : companyId,
    type: accessType,
    crudRights: finalCrudRights,
    crudRightsText: "",
    changedBy,
    changedTimestamp,
    isValid: true
  };
};

export const consolidateUsers = (users: Array<UserModel>): Array<ConsolidatedUserModel> => {
  const consolidated = new Map<string, ConsolidatedUserModel>();

  users.forEach(user => {
    let consolidatedUser = consolidated.get(user.userId);
    if (!consolidatedUser) {
      consolidatedUser = new ConsolidatedUserModel(user.userId);
      consolidated.set(user.userId, consolidatedUser);
    }

    const access: ConsolidatedUserAccess = createUserAccess(
      user.userId,
      user.companyId,
      user.crudRights,
      user.id,
      user.changedBy,
      user.changedTimestamp,
      user.type
    );

    if (access.type === UserTypeEnum.All_Assets) {
      consolidatedUser.assetTypeAccess.push(access);
    } else if (access.type === UserTypeEnum.Company_Assets) {
      consolidatedUser.assetAccess.push(access);

      if (access.companyId && access.companyId.trim() !== "" && !consolidatedUser.companyIds.includes(access.companyId)) {
        consolidatedUser.companyIds.push(access.companyId);
      }
    }
  });

  return Array.from(consolidated.values()).map(user => {
    const hasAllCompaniesAccess = user.assetAccess.some(
      access =>
        access.type === UserTypeEnum.Company_Assets &&
        (access.companyId === undefined || access.companyId === null || !access.companyId || access.companyId.trim() === "")
    );

    if (hasAllCompaniesAccess) {
      user.assetAccessDisplay = "All";
    } else {
      const validAccess = user.companyIds.length > 0 ? user.companyIds.join(", ") : "";

      user.assetAccessDisplay = validAccess || "-";
    }

    return user;
  });
};
